<template>
  <div
    :class="computedBackgroundClass"
    class="stage"
  >
    <div
      class="stage-slider"
    >
      <transition-group
        name="slide"
        tag="div"
      >
        <StageSlide
          v-for="(slide, index) in children"
          v-show="index === currentSlide"
          :key="slide.id"
          :resource="slide"
        />
      </transition-group>
    </div>
    <div
      v-touch:swipe.right="goToSlide.bind(this, 'next')"
      v-touch:swipe.left="goToSlide.bind(this, 'prev')"
      class="stage__click-area"
    >
      <div
        class="stage__click-area--left"
        @click="goToSlide('prev')"
      />
      <div
        class="stage__click-area--right"
        @click="goToSlide('next')"
      />
    </div>
    <div class="stage__pagination">
      <div
        v-for="(slide, index) in children"
        :key="slide.id"
        :class="{'stage__button--active': index === currentSlide}"
        class="stage__button"
      >
        <div
          class="stage__button-click"
          @click="goToSlide(index)"
        />
      </div>
    </div>
    <Arc
      :random-range-rotation="{ min: 30, max: 120 }"
      :random-range-position-x="{ min: 2, max: 90 }"
      :random-range-position-y="{ min: 65, max: 70 }"
    />
  </div>
</template>

<script>
import { sortBy } from 'lodash'
import editable from '@i22/frontend-cms-utils/mixins/editable'
import StageSlide from 'beisheim/components/blocks/stage-slide'
import Arc from 'beisheim/components/elements/arc'

export default {
  components: {
    StageSlide,
    Arc
  },
  mixins: [ editable ],
  props: {
    content: {
      type: Object,
      default: () => ({})
    },
    metaInformation: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      currentSlide: 0,
      interval: null,
      timer: 6000,
      focusList: ['bildung', 'gesundheit', 'kultur', 'sport']
    }
  },
  computed: {
    computedBackgroundClass () {
      const randomFocus = this.focusList[Math.floor(Math.random() * this.focusList.length)]
      return `stage--${randomFocus}`
    },
    children () {
      return sortBy(this.resource.children, 'sort_key')
    }
  },
  watch: {
    currentSlide () {
      if (this.currentSlide < 0) {
        this.currentSlide = this.resource.children.length - 1
      }
      if (this.currentSlide > this.resource.children.length - 1) {
        this.currentSlide = 0
      }
    }
  },
  methods: {
    goToSlide (index) {
      if (process.server) { return }
      if (typeof index === 'string') {
        if (index === 'next') {
          this.currentSlide += 1
        } else {
          this.currentSlide -= 1
        }
      }

      if (typeof index === 'number') {
        this.currentSlide = index
      }
      window.clearInterval(this.interval)
      if (!this.inlineEditAllowed) {
        this.autoslide()
      }
    },
    autoslide () {
      if (process.server) { return }
      this.interval = window.setInterval(() => {
        this.goToSlide('next')
      }, this.timer)
    },
    swipeRight () {
      this.goToSlide('prev')
    },
    swipeLeft () {
      this.goToSlide('next')
    }
  }
}
</script>

<style lang="scss">
@import 'beisheim/scss/breakpoints';
@import 'beisheim/scss/scale';
@import 'beisheim/scss/grid';
@import 'beisheim/scss/font';
@import 'beisheim/scss/colors';

$aspect-ratio: 0.5;

.stage {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100vw * 1.4;
  max-height: 500px;
  width: 100%;
  margin: 0 auto;
  overflow: hidden;
  text-shadow: 0 0 1rem rgba(0,0,0,0.75);
  // background-color: gray(4);

  &--bildung {
    background-color: brand(3);
  }
  &--gesundheit {
    background-color: brand(4);
  }
  &--kultur {
    background-color: brand(1);
  }
  &--sport {
    background-color: brand(2);
  }

  @include from(3) {
    height: 100vw * $aspect-ratio;
    max-height: none;
  }

  &__click-area {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    overflow: hidden;
    z-index: 10;

    &--left, &--right {
      position: absolute;
      width: 50%;
      height: 100%;
      top: 0;
      left: 0;
      cursor: url('~beisheim/assets/images/icons/stage-cursor-left.png'), pointer;
    }

    &--right {
      left: 50%;
      cursor: url('~beisheim/assets/images/icons/stage-cursor.png'), pointer;
    }
  }

  &__pagination {
    position: absolute;
    bottom: scale(4);
    @include grid-spacing(padding-left, 2);
    z-index: 15;

    @include from(5) {
      bottom: scale(8);
    }
  }

  &__button {
    position: relative;
    display: inline-block;
    width: 0.45rem;
    height: 0.45rem;
    margin-right: scale(6);
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.5);
    transition: background-color 300ms ease-in-out;
    cursor: pointer;

    &-click {
      position: absolute;
      top: -75%;
      left: -75%;
      width: 250%;
      height: 250%;
    }

    &--active {
      background-color: white;
    }
  }
}

.slide-enter-active {
  transition: all 500ms cubic-bezier(.19,0,.19,1);
  z-index: 1;
}

.slide-leave-active {
  transition: all 500ms cubic-bezier(.68,.01,1,.7);
  z-index: 0;
}

.slide-enter {
  transform: translateX(0);
  opacity: 0;

  @include from(3) {
    transform: translateX(20vw);
  }

  .headline {
    transform: translateY(50px);
    opacity: 0;
  }

  .stage-slide__paragraph {
    transform: translateY(50px);
    opacity: 0;
  }
}

.slide-leave {
  opacity: 1;
}

.slide-leave-to {
  transform: translateX(0);

  @include from(3) {
    transform: translateX(-100px);
  }
}
</style>
